import { getCalApi } from "@calcom/embed-react";
import { useEffect } from "react";
import { useTheme } from "../../hooks/useTheme";
import { LoadingTerminal } from "../ui/LoadingState";
import "./Contact.css";

export default function Contact({ owner, loading = false }) {
  const { theme } = useTheme();

  useEffect(() => {
    (async function () {
      const cal = await getCalApi({ namespace: "short" });
      cal("ui", { hideEventTypeDetails: false, layout: "month_view", theme });
    })();
  }, [theme]);

  return (
    <>
      <section id="contact" className="section contact">
        <div className="container">
          <h2 className="section-title">
            <span className="section-title__index">07</span>
            Contact
          </h2>
          {loading ? (
            <LoadingTerminal
              title="contact.sync"
              lines={[
                { key: "contact-headline", variant: "headline" },
                { key: "contact-line-1", variant: "text" },
                { key: "contact-line-2", variant: "text", style: { width: "72%" } },
                { key: "contact-line-3", variant: "text", style: { width: "54%" } },
              ]}
              actions={[
                { key: "contact-action-1" },
                { key: "contact-action-2" },
                { key: "contact-action-3", ghost: true },
              ]}
              meta={<span>Waiting for contact details and social links.</span>}
            />
          ) : (
          <div className="contact__inner">
            <div className="contact__text">
              <h3 className="contact__headline">
                Let's build something <em className="contact__highlight">great</em> together.
              </h3>
              <p className="contact__desc">
                Got a project idea, hackathon invite, or just want to connect?
                My inbox is always open.
              </p>
              <div className="contact__links">
                {owner?.email && (
                  <a href={`mailto:${owner.email}`} className="contact__link">
                    <svg className="contact__icon" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4-8 5L4 8V6l8 5 8-5v2z" />
                    </svg>
                    <span className="contact__label">Email</span>
                    <span className="contact__value">{owner.email}</span>
                  </a>
                )}
                {owner?.github && (
                  <a href={owner.github} target="_blank" rel="noopener noreferrer" className="contact__link">
                    <svg className="contact__icon" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M12 0a12 12 0 00-3.79 23.4c.6.11.82-.26.82-.58v-2.02c-3.34.73-4.04-1.61-4.04-1.61-.55-1.4-1.35-1.77-1.35-1.77-1.1-.75.08-.74.08-.74 1.22.09 1.86 1.26 1.86 1.26 1.08 1.85 2.83 1.32 3.52 1.01.11-.78.42-1.32.76-1.63-2.66-.3-5.46-1.33-5.46-5.92 0-1.31.47-2.38 1.24-3.22-.12-.3-.54-1.52.12-3.17 0 0 1-.32 3.3 1.23a11.5 11.5 0 016 0c2.28-1.55 3.29-1.23 3.29-1.23.66 1.65.24 2.87.12 3.17.77.84 1.24 1.91 1.24 3.22 0 4.6-2.8 5.62-5.47 5.92.43.37.81 1.1.81 2.22v3.29c0 .32.21.7.83.58A12 12 0 0012 0z" />
                    </svg>
                    <span className="contact__label">GitHub</span>
                    <span className="contact__value">{owner.github.replace(/^https?:\/\/(www\.)?/, "")}</span>
                  </a>
                )}
                {owner?.linkedin && (
                  <a href={owner.linkedin} target="_blank" rel="noopener noreferrer" className="contact__link">
                    <svg className="contact__icon" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M4.98 3.5a2.5 2.5 0 11.02 0H4.98zM3 8.98h4v12H3v-12zM9 8.98h3.8v1.64h.05c.53-1 1.83-2.05 3.77-2.05 4.03 0 4.77 2.65 4.77 6.10v7.31h-4v-6.48c0-1.55-.03-3.55-2.16-3.55-2.16 0-2.49 1.68-2.49 3.42v6.61H9v-12z" />
                    </svg>
                    <span className="contact__label">LinkedIn</span>
                    <span className="contact__value">{owner.linkedin.replace(/^https?:\/\/(www\.)?/, "")}</span>
                  </a>
                )}
                <button
                  type="button"
                  key={theme}
                  data-cal-namespace="short"
                  data-cal-link="rajatshenoidev/short"
                  data-cal-config={JSON.stringify({
                    layout: "month_view",
                    useSlotsViewOnSmallScreen: "true",
                    theme,
                  })}
                  className="contact__link contact__book"
                >
                  <svg className="contact__icon" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M7 2a1 1 0 0 1 1 1v1h8V3a1 1 0 1 1 2 0v1h1.5A2.5 2.5 0 0 1 22 6.5v13A2.5 2.5 0 0 1 19.5 22h-15A2.5 2.5 0 0 1 2 19.5v-13A2.5 2.5 0 0 1 4.5 4H6V3a1 1 0 0 1 1-1Zm12 7H5v10.5a.5.5 0 0 0 .5.5h15a.5.5 0 0 0 .5-.5V9ZM5 7h14V6.5a.5.5 0 0 0-.5-.5h-1.5v1a1 1 0 1 1-2 0V6h-8v1a1 1 0 0 1-2 0V6H4.5a.5.5 0 0 0-.5.5V7Z" />
                  </svg>
                  <span className="contact__label">Book an appointment</span>
                  <span className="contact__value contact__value--muted">30 min · Google Meet</span>
                </button>
              </div>
            </div>

            <div className="contact__snippet">
              <div className="contact__snippet-line" aria-hidden="true">
                <span className="contact__prompt">$</span> whoami
              </div>
              <div className="contact__snippet-out" aria-hidden="true">
                {owner?.name || "dev"} — always up for a chat
              </div>
              <div className="contact__snippet-line" aria-hidden="true">
                <span className="contact__prompt">$</span> send_message --now
              </div>
              <div className="contact__snippet-out">
                <a href={`mailto:${owner?.email || "#"}`} className="contact__snippet-cta">
                  {owner?.email || "…"}
                </a>
              </div>
            </div>
          </div>
          )}
        </div>
      </section>

      <footer className="footer">
        <div className="container footer__inner">
          <p className="footer__copy">
            © {new Date().getFullYear()} {owner?.name || "dev"}
          </p>
          <p className="footer__stack">designed &amp; built with react · vite · firebase</p>
        </div>
      </footer>
    </>
  );
}
